import React, { useState } from 'react';
import { ElementType } from '../types';
import { useDraggable } from '@dnd-kit/core';

interface ElementPaletteProps {
  onDragStart?: (elementType: ElementType) => void;
  onClickAdd?: (elementType: ElementType, variant?: string) => void;
  onAddModel?: (modelName: string) => void;
}

const ElementPalette: React.FC<ElementPaletteProps> = ({ onDragStart, onClickAdd, onAddModel }) => {
  console.log('[ElementPalette] Rendered with props:', { onDragStart: !!onDragStart, onClickAdd: !!onClickAdd, onAddModel: !!onAddModel });

  const [openAccordion, setOpenAccordion] = useState<'elements' | 'models'>('elements');

  const elements: { type: ElementType; name: string; icon: React.ReactNode; variant?: string }[] = [
    { type: ElementType.Options, name: 'Quiz Simples', icon: <span>☰</span>, variant: 'single' },
    { type: ElementType.Options, name: 'Múltipla Escolha', icon: <span>☰</span>, variant: 'multi' },
    { type: ElementType.Options, name: 'Quiz Sim/Não', icon: <span>☰</span>, variant: 'binary' },
    { type: ElementType.Title, name: 'Título', icon: <span className="font-bold">T</span> },
    { type: ElementType.Text, name: 'Texto', icon: <span>Tx</span> },
    { type: ElementType.Alert, name: 'Alerta', icon: <span>⚠️</span> },
    { type: ElementType.Image, name: 'Imagem', icon: <span>🖼️</span> },
    {
      type: ElementType.Carousel, name: 'Carrossel', icon: (
        <div className="flex flex-col items-center leading-none">
          <span>🖼️</span>
          <span className="text-xs">•••</span>
        </div>
      )
    },
    {
      type: ElementType.BeforeAfter, name: 'Antes/Depois', icon: (
        <div className="flex items-center justify-center w-6 h-6">
          <svg viewBox="0 0 24 24" className="w-6 h-6 text-slate-600">
            <path d="M12 3v18" stroke="currentColor" strokeWidth="2" />
            <path d="M9 12l-3 3M9 12l-3-3" stroke="currentColor" strokeWidth="1.5" fill="none" />
            <path d="M15 12l3 3M15 12l3-3" stroke="currentColor" strokeWidth="1.5" fill="none" />
          </svg>
        </div>
      )
    },
    { type: ElementType.Audio, name: 'Áudio', icon: <span>🔊</span> },
    { type: ElementType.Video, name: 'Vídeo', icon: <span>🎥</span> },
    { type: ElementType.Form, name: 'Formulário', icon: <span>📝</span> },
    { type: ElementType.List, name: 'Lista', icon: <span>📋</span> },
    {
      type: ElementType.Loading, name: 'Loading', icon: (
        <svg width="36" height="16" viewBox="0 0 36 16" className="text-slate-600">
          <rect x="1" y="6" width="34" height="4" rx="2" ry="2" fill="none" stroke="currentColor" strokeWidth="2" />
          <rect x="1" y="6" width="18" height="4" rx="2" ry="2" fill="currentColor" />
        </svg>
      )
    },
    {
      type: ElementType.Level, name: 'Nível', icon: (
        <svg width="36" height="16" viewBox="0 0 36 16" className="text-slate-600">
          <rect x="1" y="7" width="34" height="2" rx="1" ry="1" fill="currentColor" opacity="0.3" />
          <rect x="1" y="7" width="24" height="2" rx="1" ry="1" fill="currentColor" />
          <circle cx="25" cy="8" r="3" fill="white" stroke="currentColor" strokeWidth="2" />
        </svg>
      )
    },
    { type: ElementType.Chart, name: 'Gráfico', icon: <span>📊</span> },
    { type: ElementType.Report, name: 'Relatório', icon: <span>🧾</span> },
    { type: ElementType.Timer, name: 'Timer', icon: <span>⏱️</span> },
    { type: ElementType.Testimonial, name: 'Depoimento', icon: <span>⭐</span> },
    {
      type: ElementType.FbComments, name: 'Comentários', icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600">
          <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
        </svg>
      )
    },
    { type: ElementType.Seal, name: 'Selo', icon: <span>🏅</span> },
    { type: ElementType.QA, name: 'Q&A', icon: <span>❓</span> },
    {
      type: ElementType.Roulette, name: 'Roleta', icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-600">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 2v20" />
          <path d="M3.34 7l17.32 10" />
          <path d="M3.34 17l17.32-10" />
          <circle cx="12" cy="12" r="2" fill="currentColor" />
          <path d="M12 2L10 0h4L12 2z" fill="currentColor" stroke="none" />
        </svg>
      )
    },
    {
      type: ElementType.SlotMachine, name: 'Caça Niqueis', icon: (
        <span className="text-xl">🎰</span>
      )
    },
    { type: ElementType.Price, name: 'Preço', icon: <span>💲</span> },
    {
      type: ElementType.Button,
      name: 'Botão',
      icon: (
        <svg width="36" height="16" viewBox="0 0 36 16" className="text-slate-600">
          <rect x="1" y="3" width="34" height="10" rx="6" ry="6" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
      )
    },
    { type: ElementType.Spacer, name: 'Espaçador', icon: <span>━</span> },
  ];

  const models = [
    { id: 'cover', name: 'Capa' },
    { id: 'question-image-2x2', name: 'Pergunta Imagem 2x2' },
    { id: 'question-image-2x1', name: 'Pergunta Imagem 2x1' },
    { id: 'question-emoji-buttons', name: 'Pergunta Botões Emojis' },
    { id: 'menu-image-check', name: 'Cardápio Imagem e Check' },
    { id: 'basic-form', name: 'Form Básico' }
  ];

  return (
    <div className="p-4 bg-slate-50 rounded-lg space-y-4">
      {/* Elementos Accordion */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <button
          onClick={() => setOpenAccordion('elements')}
          className="w-full flex justify-between items-center p-3 text-left bg-white hover:bg-slate-50 transition-colors"
        >
          <h3 className="font-bold text-lg text-slate-800">Elementos</h3>
          <svg
            className={`w-5 h-5 text-slate-600 transition-transform duration-200 ${openAccordion === 'elements' ? 'rotate-180' : ''}`}
            xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${openAccordion === 'elements' ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="p-3 grid grid-cols-3 gap-2 border-t border-slate-100">
            {elements.map((element, idx) => (
              <DraggableTile
                key={`${element.type}-${element.variant || 'default'}-${idx}`}
                id={`palette-${element.type}-${element.variant || 'default'}`}
                label={element.name}
                icon={element.icon}
                onStart={() => onDragStart?.(element.type)}
                onClick={() => onClickAdd?.(element.type, element.variant)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Modelos Accordion */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <button
          onClick={() => setOpenAccordion('models')}
          className="w-full flex justify-between items-center p-3 text-left bg-white hover:bg-slate-50 transition-colors"
        >
          <h3 className="font-bold text-lg text-slate-800">Modelos</h3>
          <svg
            className={`w-5 h-5 text-slate-600 transition-transform duration-200 ${openAccordion === 'models' ? 'rotate-180' : ''}`}
            xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${openAccordion === 'models' ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="p-3 grid grid-cols-3 gap-2 border-t border-slate-100">
            {models.map((model) => (
              <ModelTile
                key={model.id}
                label={model.name}
                onClick={() => {
                  console.log(`[ElementPalette] Clicked model: ${model.id}`);
                  if (onAddModel) {
                    console.log(`[ElementPalette] Calling onAddModel for: ${model.id}`);
                    onAddModel(model.id);
                  } else {
                    console.error('[ElementPalette] onAddModel prop is undefined!');
                  }
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const DraggableTile: React.FC<{ id: string; label: string; icon: React.ReactNode; onStart?: () => void; onClick?: () => void }> = ({ id, label, icon, onStart, onClick }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onMouseDown={onStart}
      onClick={onClick}
      className={`flex flex-col items-center justify-center p-3 bg-white border rounded-lg cursor-grab transition-colors text-center ${isDragging ? 'opacity-50' : 'hover:bg-slate-50'} border-slate-200 hover:border-slate-300`}
    >
      <div className="mb-1 flex items-center justify-center h-8 w-full text-slate-600">{icon}</div>
      <span className="block text-xs font-medium text-slate-600 text-center leading-tight">{label}</span>
    </div>
  );
};

const ModelTile: React.FC<{ label: string; onClick?: () => void }> = ({ label, onClick }) => {
  return (
    <div
      onClick={(e) => {
        // Prevent default behavior but allow event propagation if needed, 
        // though stopping propagation is usually safer for nested interactive elements.
        e.preventDefault();
        e.stopPropagation();
        console.log('[ElementPalette] ModelTile div clicked');
        if (onClick) {
          console.log('[ElementPalette] Executing onClick handler');
          onClick();
        } else {
          console.warn('[ElementPalette] No onClick handler provided for ModelTile');
        }
      }}
      className="flex flex-col items-center justify-center p-3 bg-white border rounded-lg cursor-pointer transition-colors text-center hover:bg-slate-50 border-slate-200 hover:border-slate-300 w-full min-h-[84px]"
    >
      <span className="block text-xs font-medium text-slate-600 text-center leading-tight">{label}</span>
    </div>
  );
};

export default ElementPalette;
